import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { getBookingDrawerTheme } from '../../utils/bookingDrawerLayout';
import {
  formatBookingAuditTime,
  getBookingAuditActorChipSx,
  getBookingAuditDrawerHeaderSx,
  getBookingAuditEmptyStateSx,
  getBookingAuditEventCardSx,
  getBookingAuditEventTypeChipSx,
  getBookingAuditMetaChipSx,
  getBookingAuditRefreshButtonSx,
  getBookingAuditRefreshRowSx,
  getBookingAuditTimelineAvatarSx,
  getBookingAuditTimelineConnectorSx,
  getGuestDrawerMetaChipSx,
  getStockAuditBalanceChipSx,
  getStockAuditDrawerPaperSx,
  getStockAuditEventPresentation,
  getStockAuditSummaryChipSx,
} from '../../utils/stockAuditLayout';
import { STOCK_ACCENT } from '../../utils/stockMobileLayout';
import { getStockItemActivity } from '../../services/api';

const PAGE_SIZE = 10;

const formatTransferLabel = (value, unit) => {
  const num = Number(value);
  const u = String(unit || '').trim();
  if (!Number.isFinite(num) || num === 0) {
    return u ? `0 ${u}` : '0';
  }
  const absText = String(Math.abs(num)).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  const signed = num > 0 ? `+${absText}` : `-${absText}`;
  return u ? `${signed} ${u}` : signed;
};

const formatQtyLabel = (value, unit) => {
  const u = String(unit || '').trim();
  const num = Number(value);
  let text = String(value ?? '0');
  if (Number.isFinite(num)) {
    text = String(num).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }
  return u ? `${text} ${u}` : text;
};

const LedgerEventRow = ({ event, isDark, displayUnit, isLast }) => {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const presentation = getStockAuditEventPresentation(event.event_type);
  const actorLabel = event.created_by || 'system';
  const qtyIn = Number(event.quantity_in || 0);
  const qtyOut = Number(event.quantity_out || 0);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 2 }}>
      <Box sx={{ width: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={getBookingAuditTimelineAvatarSx(isDark, presentation.color)}>
          {presentation.icon}
        </Avatar>
        {!isLast && <Box sx={getBookingAuditTimelineConnectorSx(isDark)} />}
      </Box>

      <Paper elevation={0} sx={getBookingAuditEventCardSx(isDark)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="subtitle2"
            sx={{ color: isDark ? drawerTheme.text : '#0f172a', fontWeight: 700 }}
          >
            {event.title || 'Stock Activity'}
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
            {formatBookingAuditTime(event.created_at, event.created_at_display)}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: isDark ? drawerTheme.subtleText : '#475569', mt: 0.5 }}
        >
          {event.description || 'Stock movement captured.'}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`By ${actorLabel}`} sx={getBookingAuditActorChipSx(isDark)} />
          {event.reference_number && event.reference_number !== '—' ? (
            <Chip size="small" label={event.reference_number} sx={getBookingAuditMetaChipSx(isDark)} />
          ) : null}
          {qtyIn > 0 ? (
            <Chip
              size="small"
              label={`+${event.quantity_in} ${displayUnit}`.trim()}
              sx={getStockAuditSummaryChipSx(isDark, '#66d4a8')}
            />
          ) : null}
          {qtyOut > 0 ? (
            <Chip
              size="small"
              label={`-${event.quantity_out} ${displayUnit}`.trim()}
              sx={getStockAuditSummaryChipSx(isDark, '#ef5350')}
            />
          ) : null}
          <Chip
            size="small"
            label={`Bal ${event.running_balance} ${displayUnit}`.trim()}
            sx={getStockAuditBalanceChipSx(isDark)}
          />
          <Chip
            size="small"
            label={(event.event_type || 'activity').replace(/_/g, ' ')}
            sx={getBookingAuditEventTypeChipSx(isDark, presentation.color)}
          />
        </Box>
      </Paper>
    </Box>
  );
};

const StockItemLedgerDrawer = ({
  open,
  onClose,
  itemId,
  itemName,
  purchaseFor,
  purchaseForLabel,
  isDark,
}) => {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const scrollRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const [events, setEvents] = useState([]);
  const [itemMeta, setItemMeta] = useState(null);
  const [poolSummary, setPoolSummary] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const safeItemId = useMemo(() => {
    const parsed = Number(itemId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [itemId]);

  const buildParams = useCallback(
    (cursor) => {
      const params = { limit: PAGE_SIZE };
      if (purchaseFor) {
        params.pool = purchaseFor;
      }
      if (cursor) {
        params.cursor = cursor;
      }
      return params;
    },
    [purchaseFor],
  );

  const fetchPage = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (!safeItemId) {
        setError('Invalid item for stock ledger.');
        if (!append) {
          setEvents([]);
        }
        return;
      }

      if (append) {
        if (loadingMoreRef.current) {
          return;
        }
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const data = await getStockItemActivity(safeItemId, buildParams(cursor));
        const pageEvents = Array.isArray(data?.events) ? data.events : [];
        setItemMeta(data?.item || null);
        setPoolSummary(data?.pool_summary || null);
        setMonthSummary(data?.month_summary || null);
        setHasMore(Boolean(data?.pagination?.has_more));
        setNextCursor(data?.pagination?.next_cursor || null);
        setTotalCount(Number(data?.pagination?.total_count) || pageEvents.length);

        setEvents((prev) => {
          if (!append) {
            return pageEvents;
          }
          const seen = new Set(prev.map((ev) => String(ev.id)));
          const merged = [...prev];
          pageEvents.forEach((ev) => {
            const key = String(ev.id);
            if (!seen.has(key)) {
              seen.add(key);
              merged.push(ev);
            }
          });
          return merged;
        });
      } catch (err) {
        if (!append) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load stock ledger.');
          setEvents([]);
        }
      } finally {
        if (append) {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [safeItemId, buildParams],
  );

  const resetAndFetch = useCallback(() => {
    setEvents([]);
    setHasMore(false);
    setNextCursor(null);
    setTotalCount(0);
    fetchPage({ append: false });
  }, [fetchPage]);

  useEffect(() => {
    if (open) {
      resetAndFetch();
      return;
    }
    setError(null);
    setEvents([]);
    setItemMeta(null);
    setPoolSummary(null);
    setMonthSummary(null);
    setHasMore(false);
    setNextCursor(null);
    setTotalCount(0);
  }, [open, resetAndFetch]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreRef.current;
    if (!open || !root || !sentinel || !hasMore || loading || loadingMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && nextCursor && !loadingMoreRef.current) {
          fetchPage({ cursor: nextCursor, append: true });
        }
      },
      { root, rootMargin: '120px', threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, nextCursor, loading, loadingMore, fetchPage]);

  const displayName = itemName || itemMeta?.name || 'Stock Item';
  const displayUnit = itemMeta?.unit || monthSummary?.unit || '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: getStockAuditDrawerPaperSx(isDark),
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={getBookingAuditDrawerHeaderSx(isDark)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <FactCheckIcon sx={{ color: STOCK_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{ color: isDark ? drawerTheme.text : '#0f172a', fontWeight: 700 }}
            >
              Stock Ledger
            </Typography>
            <Chip size="small" label={displayName} sx={getGuestDrawerMetaChipSx(isDark)} />
            {purchaseForLabel ? (
              <Chip size="small" label={purchaseForLabel} sx={getBookingAuditMetaChipSx(isDark)} />
            ) : null}
            {displayUnit ? (
              <Chip size="small" label={displayUnit} sx={getBookingAuditMetaChipSx(isDark)} />
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={getBookingAuditRefreshRowSx(isDark)}>
          <Button
            variant="text"
            onClick={resetAndFetch}
            disabled={loading || loadingMore}
            sx={getBookingAuditRefreshButtonSx(isDark)}
          >
            {loading ? 'Refreshing timeline...' : 'Refresh timeline'}
          </Button>

          {monthSummary ? (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', display: 'block', mb: 0.75 }}
              >
                {monthSummary.month_label} — monthly summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`Opening ${formatQtyLabel(monthSummary.opening_stock, displayUnit)}`.trim()}
                  sx={getStockAuditSummaryChipSx(isDark, '#90caf9')}
                />
                <Chip
                  size="small"
                  label={`Added ${formatQtyLabel(monthSummary.stock_added, displayUnit)}`.trim()}
                  sx={getStockAuditSummaryChipSx(isDark, '#66d4a8')}
                />
                <Chip
                  size="small"
                  label={`Issued ${formatQtyLabel(monthSummary.stock_issued, displayUnit)}`.trim()}
                  sx={getStockAuditSummaryChipSx(isDark, '#ef5350')}
                />
                <Chip
                  size="small"
                  label={`Adjust ${formatTransferLabel(monthSummary.stock_adjusted, displayUnit)}`.trim()}
                  sx={getStockAuditSummaryChipSx(isDark, '#ab47bc')}
                />
                <Chip
                  size="small"
                  label={`Transfer ${formatTransferLabel(monthSummary.stock_transferred, displayUnit)}`.trim()}
                  sx={getStockAuditSummaryChipSx(isDark, '#ff9800')}
                />
                <Chip
                  size="small"
                  label={`Closing ${formatQtyLabel(monthSummary.closing_stock, displayUnit)}`.trim()}
                  sx={getStockAuditBalanceChipSx(isDark)}
                />
              </Box>
            </Box>
          ) : null}

          {poolSummary && !monthSummary ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                size="small"
                label={`Available ${poolSummary.available} ${displayUnit}`.trim()}
                sx={getStockAuditBalanceChipSx(isDark)}
              />
            </Box>
          ) : null}

          {totalCount > 0 ? (
            <Typography
              variant="caption"
              sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', display: 'block', mt: 1 }}
            >
              Showing {events.length} of {totalCount} events (newest first)
            </Typography>
          ) : null}
        </Box>

        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading && events.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 4 }).map((_, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1.5 }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="rounded" height={120} sx={{ flex: 1 }} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && events.length === 0 && (
            <Paper elevation={0} sx={getBookingAuditEmptyStateSx(isDark)}>
              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
                No stock movements recorded for this item yet.
              </Typography>
            </Paper>
          )}

          {events.map((event, index) => (
            <LedgerEventRow
              key={event.id || `${event.event_type}-${index}`}
              event={event}
              isDark={isDark}
              displayUnit={displayUnit}
              isLast={index === events.length - 1 && !hasMore && !loadingMore}
            />
          ))}

          {hasMore && (
            <Box
              ref={loadMoreRef}
              sx={{ py: 2, display: 'flex', justifyContent: 'center', minHeight: 48 }}
            >
              {loadingMore && <CircularProgress size={24} sx={{ color: STOCK_ACCENT }} />}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default StockItemLedgerDrawer;
